import { useState, useCallback, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { PackagePlus, Pill, User, MapPin, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Camera, Loader2, Sparkles, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageForAI } from '@/lib/imageUtils';

export default function RicevimentoFarmaciDialog({ open, onOpenChange, ospiteId, onComplete }) {
  const [ospite, setOspite] = useState(null);
  const [scorte, setScorte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rifForm, setRifForm] = useState({ scatole: '1', unitaConf: '', manuale: false });
  const [saving, setSaving] = useState(false);

  // Scan photo state
  const [scanMode, setScanMode] = useState(false); // true = scanning view
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async (oid) => {
    setLoading(true);
    try {
      const [ospRes, scorteRes] = await Promise.all([
        api.get(`/ospiti/${oid}`),
        api.get(`/scorte/ospite/${oid}`)
      ]);
      setOspite(ospRes.data);
      setScorte(scorteRes.data);
    } catch {
      toast.error('Errore nel caricamento dati ospite');
      setOspite(null);
      setScorte([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open && ospiteId) {
      fetchData(ospiteId);
    }
    if (!open) {
      setOspite(null);
      setScorte([]);
      setExpandedId(null);
      setScanMode(false);
      setScanResult(null);
      setPreviewImg(null);
    }
  }, [open, ospiteId, fetchData]);

  const toggleExpand = (terapiaId, item) => {
    if (expandedId === terapiaId) {
      setExpandedId(null);
    } else {
      setExpandedId(terapiaId);
      const confUnita = item.confezione_unita || scanResult?.unita_confezione || 0;
      setRifForm({
        scatole: '1',
        unitaConf: confUnita > 0 ? String(confUnita) : '',
        manuale: confUnita === 0
      });
    }
  };

  const rifTotaleUnita = () => {
    const scatole = parseInt(rifForm.scatole) || 0;
    const unita = parseInt(rifForm.unitaConf) || 0;
    return scatole * unita;
  };

  const handleRifornimento = async (item) => {
    const totale = rifTotaleUnita();
    if (totale <= 0) { toast.error('Inserisci quantita valida'); return; }
    setSaving(true);
    try {
      const payload = { quantita: totale };
      const unitaConf = parseInt(rifForm.unitaConf) || 0;
      if (unitaConf > 0) payload.confezione_unita = unitaConf;
      await api.put(`/scorte/${item.terapia_id}/rifornimento`, payload);
      toast.success(`${item.farmaco}: +${totale} unita registrate`);
      setExpandedId(null);
      setScanResult(null);
      setScanMode(false);
      setPreviewImg(null);
      const res = await api.get(`/scorte/ospite/${ospiteId}`);
      setScorte(res.data);
      if (onComplete) onComplete();
    } catch {
      toast.error('Errore aggiornamento scorta');
    }
    setSaving(false);
  };

  // === PHOTO SCAN ===
  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setPreviewImg(dataUrl);
      setScanMode(true);
      setScanning(true);
      setScanResult(null);

      try {
        // Compress image before sending (800px, quality 0.6)
        const base64 = await compressImageForAI(dataUrl);

        const res = await api.post('/farmaci/riconosci-immagine', {
          image_base64: base64,
          ospite_id: ospiteId
        });
        setScanResult(res.data);
        if (res.data.parsed && res.data.farmaco) {
          const nome = res.data.farmaco.nome_commerciale || res.data.farmaco.principio_attivo || 'Farmaco';
          toast.success(`Riconosciuto: ${nome}`);
          // Auto-expand the best match
          if (res.data.matches?.length > 0) {
            const best = res.data.matches[0];
            const confUnita = best.confezione_unita || res.data.unita_confezione || 0;
            setExpandedId(best.terapia_id);
            setRifForm({
              scatole: '1',
              unitaConf: confUnita > 0 ? String(confUnita) : (res.data.unita_confezione ? String(res.data.unita_confezione) : ''),
              manuale: confUnita === 0 && !res.data.unita_confezione
            });
          }
        } else {
          toast.info('Farmaco non riconosciuto, seleziona manualmente');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 402 || detail.includes('Credito') || detail.includes('Budget')) {
          toast.error('Credito AI esaurito. Ricarica il saldo in Profilo → Universal Key.');
        } else {
          toast.error('Errore nel riconoscimento farmaco');
        }
        setScanResult({ parsed: false, farmaco: null, matches: [] });
      }
      setScanning(false);
    };
    reader.readAsDataURL(file);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const exitScanMode = () => {
    setScanMode(false);
    setScanResult(null);
    setPreviewImg(null);
    setExpandedId(null);
  };

  // Sort: in esaurimento first, then by scan match score
  const sortedScorte = [...scorte].sort((a, b) => {
    // If scan result has matches, prioritize them
    if (scanResult?.matches?.length > 0) {
      const aMatch = scanResult.matches.find(m => m.terapia_id === a.terapia_id);
      const bMatch = scanResult.matches.find(m => m.terapia_id === b.terapia_id);
      const aScore = aMatch?.match_score || 0;
      const bScore = bMatch?.match_score || 0;
      if (aScore !== bScore) return bScore - aScore;
    }
    if (a.in_esaurimento && !b.in_esaurimento) return -1;
    if (!a.in_esaurimento && b.in_esaurimento) return 1;
    return a.giorni_rimasti - b.giorni_rimasti;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="ricevimento-dialog-title">
            <PackagePlus className="w-5 h-5 text-emerald-600" />
            Ricevimento Farmaci
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>
        ) : !ospite ? (
          <div className="py-8 text-center text-slate-400 text-sm">Ospite non trovato</div>
        ) : (
          <div className="space-y-4">
            {/* Ospite header */}
            <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3" data-testid="ricevimento-ospite-info">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-brand" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{ospite.cognome} {ospite.nome}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />
                  Camera {ospite.stanza || '-'} / Letto {ospite.letto || '-'}
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-700 text-xs">{scorte.length} farmaci</Badge>
            </div>

            {/* Photo scan button */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoCapture}
                data-testid="foto-farmaco-input"
              />
              {!scanMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-violet-200 text-violet-700 hover:bg-violet-50"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="foto-farmaco-btn"
                >
                  <Camera className="w-4 h-4 mr-1.5" />
                  Fotografa Scatola Farmaco
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-slate-200 text-slate-600"
                  onClick={exitScanMode}
                  data-testid="exit-scan-mode-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Torna alla lista completa
                </Button>
              )}
            </div>

            {/* Scan result banner */}
            {scanning && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 text-center" data-testid="scan-loading">
                <Loader2 className="w-6 h-6 mx-auto mb-2 text-violet-500 animate-spin" />
                <p className="text-sm font-medium text-violet-700">Analisi immagine in corso...</p>
                <p className="text-xs text-violet-400">GPT-4o sta riconoscendo il farmaco</p>
              </div>
            )}

            {scanResult?.parsed && scanResult.farmaco && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2" data-testid="scan-result-banner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    <span className="text-xs font-semibold text-violet-700">Farmaco riconosciuto</span>
                  </div>
                  <button onClick={() => { setScanResult(null); setPreviewImg(null); }} className="text-slate-400 hover:text-slate-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {previewImg && (
                  <div className="flex justify-center">
                    <img src={previewImg} alt="Foto farmaco" className="h-20 rounded-md object-contain border border-violet-200" data-testid="scan-preview-img" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {scanResult.farmaco.nome_commerciale && (
                    <div><span className="text-slate-400">Nome:</span> <span className="font-semibold text-slate-700">{scanResult.farmaco.nome_commerciale}</span></div>
                  )}
                  {scanResult.farmaco.principio_attivo && (
                    <div><span className="text-slate-400">P.A.:</span> <span className="font-semibold text-slate-700">{scanResult.farmaco.principio_attivo}</span></div>
                  )}
                  {scanResult.farmaco.dosaggio && (
                    <div><span className="text-slate-400">Dosaggio:</span> <span className="font-semibold text-slate-700">{scanResult.farmaco.dosaggio}</span></div>
                  )}
                  {scanResult.farmaco.forma && (
                    <div><span className="text-slate-400">Forma:</span> <span className="font-semibold text-slate-700">{scanResult.farmaco.forma}</span></div>
                  )}
                  {scanResult.farmaco.unita_confezione && (
                    <div><span className="text-slate-400">Unita/Conf:</span> <span className="font-bold text-emerald-600">{scanResult.farmaco.unita_confezione}</span></div>
                  )}
                  {scanResult.farmaco.produttore && (
                    <div><span className="text-slate-400">Produttore:</span> <span className="font-semibold text-slate-700">{scanResult.farmaco.produttore}</span></div>
                  )}
                </div>
                {scanResult.matches?.length > 0 ? (
                  <p className="text-[10px] text-emerald-600 font-medium">
                    Corrispondenza trovata con {scanResult.matches.length} terapia/e attiva/e
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-600 font-medium">
                    Nessuna corrispondenza con le terapie attive. Seleziona manualmente.
                  </p>
                )}
              </div>
            )}

            {scanResult && !scanResult.parsed && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="scan-error-banner">
                <p className="text-sm text-amber-700">Non riesco a leggere il farmaco dalla foto.</p>
                <p className="text-xs text-amber-500">Prova con una foto piu nitida o seleziona manualmente.</p>
              </div>
            )}

            <Separator />

            {/* Drug list */}
            {sortedScorte.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-sm">
                <Pill className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nessuna terapia attiva
              </div>
            ) : (
              <div className="space-y-2" data-testid="ricevimento-farmaci-list">
                {sortedScorte.map((item) => {
                  const isExpanded = expandedId === item.terapia_id;
                  const isLow = item.in_esaurimento;
                  const isCritical = item.giorni_rimasti <= 3;
                  const matchInfo = scanResult?.matches?.find(m => m.terapia_id === item.terapia_id);
                  const isMatched = matchInfo && matchInfo.match_score > 0;
                  return (
                    <Card
                      key={item.terapia_id}
                      className={`transition-all ${isMatched ? 'border-violet-300 bg-violet-50/40 ring-1 ring-violet-200' : isCritical ? 'border-red-200 bg-red-50/30' : isLow ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}
                      data-testid={`ricevimento-farmaco-${item.terapia_id}`}
                    >
                      <CardContent className="p-3">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => toggleExpand(item.terapia_id, item)}
                          data-testid={`ricevimento-toggle-${item.terapia_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold text-slate-800">{item.farmaco}</p>
                              {isMatched && <Sparkles className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                            </div>
                            <p className="text-xs text-slate-400">{item.dosaggio} - {item.via} {item.orari?.length > 0 && `(${item.orari.join(', ')})`}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-right">
                              <p className={`text-sm font-bold ${isCritical ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {item.scorta_attuale} u
                              </p>
                              <p className="text-[10px] text-slate-400">
                                {item.giorni_rimasti >= 999 ? 'al bisogno' : `${item.giorni_rimasti} gg`}
                              </p>
                            </div>
                            {isLow && !isMatched && (
                              <AlertTriangle className={`w-4 h-4 ${isCritical ? 'text-red-500' : 'text-amber-500'}`} />
                            )}
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-slate-200 space-y-3" data-testid={`ricevimento-form-${item.terapia_id}`}>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">N. Scatole</Label>
                                <Input
                                  type="number" min="1"
                                  value={rifForm.scatole}
                                  onChange={e => setRifForm(f => ({ ...f, scatole: e.target.value }))}
                                  data-testid="ricevimento-scatole"
                                  placeholder="Es. 2"
                                />
                              </div>
                              <div>
                                <Label className="text-xs flex items-center gap-1">
                                  Unita/Conf.
                                  {((item.confezione_unita > 0 && !rifForm.manuale) || (scanResult?.unita_confezione && !rifForm.manuale)) && (
                                    <Badge className="text-[8px] bg-emerald-100 text-emerald-700">
                                      {scanResult?.unita_confezione && !item.confezione_unita ? 'AI' : 'auto'}
                                    </Badge>
                                  )}
                                </Label>
                                <Input
                                  type="number" min="1"
                                  value={rifForm.unitaConf}
                                  onChange={e => setRifForm(f => ({ ...f, unitaConf: e.target.value, manuale: true }))}
                                  data-testid="ricevimento-unita-conf"
                                  placeholder="Es. 30"
                                  className={!rifForm.manuale && rifForm.unitaConf ? 'border-emerald-300 bg-emerald-50/30' : ''}
                                />
                              </div>
                            </div>

                            {rifTotaleUnita() > 0 && (
                              <div className="bg-emerald-50 rounded-lg p-2 text-center">
                                <p className="text-xs text-slate-500">Totale da aggiungere</p>
                                <p className="text-lg font-bold text-emerald-700">+{rifTotaleUnita()} unita</p>
                                <p className="text-[10px] text-emerald-600">Nuova scorta: {item.scorta_attuale + rifTotaleUnita()}</p>
                              </div>
                            )}

                            <Button
                              size="sm"
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={rifTotaleUnita() <= 0 || saving}
                              onClick={() => handleRifornimento(item)}
                              data-testid={`ricevimento-conferma-${item.terapia_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {saving ? 'Salvataggio...' : 'Registra Arrivo'}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
